// What an App reads when one of its live-data operations is refused or fails: one plain sentence per kind of
// problem, never a code or an object. The host attaches it as the SDK error's `message` in the reader's language
// (ui/workspace/apps/runtime/runtime-error-copy.ts). Namespace `app-runtime-errors`; copy by DESIGN.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  connection: 'The connected source isn’t available right now. Its owner can reconnect it.',
  connectionChanged: 'The connected source has changed. Reopen this App to use it.',
  access: 'You no longer have access to this data.',
  appChanged: 'This App has changed. Reopen it to see the latest version.',
  busy: 'Too many requests right now. Try again in a moment.',
  tooLarge: 'That request is too large. Ask for less at a time.',
  slow: 'This took too long to load. Try again, or ask for less at a time.',
  invalid: 'This App asked for something it can’t do. Ask in chat to fix it.',
  unavailable: 'This couldn’t load right now. Try again.',
};
export type AppRuntimeErrorKey = keyof typeof en;
type Copy = Record<AppRuntimeErrorKey, string>;

const bg: Copy = {
  connection:
    'Свързаният източник не е достъпен в момента. Собственикът му може да го свърже отново.',
  connectionChanged:
    'Свързаният източник е променен. Отворете приложението отново, за да го използвате.',
  access: 'Вече нямате достъп до тези данни.',
  appChanged: 'Приложението е променено. Отворете го отново, за да видите последната версия.',
  busy: 'Твърде много заявки в момента. Опитайте отново след малко.',
  tooLarge: 'Заявката е твърде голяма. Поискайте по-малко наведнъж.',
  slow: 'Зареждането отне твърде дълго. Опитайте отново или поискайте по-малко наведнъж.',
  invalid: 'Приложението поиска нещо, което не може да направи. Помолете в чата да бъде поправено.',
  unavailable: 'Не успяхме да заредим това в момента. Опитайте отново.',
};
const cs: Copy = {
  connection: 'Propojený zdroj teď není dostupný. Jeho vlastník ho může znovu připojit.',
  connectionChanged: 'Propojený zdroj se změnil. Aplikaci znovu otevřete, abyste ho mohli použít.',
  access: 'K těmto datům už nemáte přístup.',
  appChanged: 'Aplikace se změnila. Znovu ji otevřete a uvidíte nejnovější verzi.',
  busy: 'Právě je příliš mnoho požadavků. Zkuste to za chvíli znovu.',
  tooLarge: 'Požadavek je příliš velký. Žádejte méně najednou.',
  slow: 'Načítání trvalo příliš dlouho. Zkuste to znovu nebo žádejte méně najednou.',
  invalid: 'Aplikace žádala o něco, co nemůže udělat. Požádejte v chatu o opravu.',
  unavailable: 'Teď se to nepodařilo načíst. Zkuste to znovu.',
};
const da: Copy = {
  connection: 'Den forbundne kilde er ikke tilgængelig lige nu. Dens ejer kan forbinde den igen.',
  connectionChanged: 'Den forbundne kilde er ændret. Åbn appen igen for at bruge den.',
  access: 'Du har ikke længere adgang til disse data.',
  appChanged: 'Appen er ændret. Åbn den igen for at se den nyeste version.',
  busy: 'For mange anmodninger lige nu. Prøv igen om lidt.',
  tooLarge: 'Anmodningen er for stor. Bed om mindre ad gangen.',
  slow: 'Det tog for lang tid at indlæse. Prøv igen, eller bed om mindre ad gangen.',
  invalid: 'Appen bad om noget, den ikke kan. Spørg i chatten for at rette det.',
  unavailable: 'Det kunne ikke indlæses lige nu. Prøv igen.',
};
const de: Copy = {
  connection:
    'Die verbundene Quelle ist gerade nicht verfügbar. Ihr Eigentümer kann sie erneut verbinden.',
  connectionChanged:
    'Die verbundene Quelle hat sich geändert. Öffnen Sie die App erneut, um sie zu verwenden.',
  access: 'Sie haben keinen Zugriff mehr auf diese Daten.',
  appChanged: 'Diese App wurde geändert. Öffnen Sie sie erneut, um die neueste Version zu sehen.',
  busy: 'Derzeit gibt es zu viele Anfragen. Versuchen Sie es gleich noch einmal.',
  tooLarge: 'Diese Anfrage ist zu groß. Fragen Sie weniger auf einmal ab.',
  slow: 'Das Laden hat zu lange gedauert. Versuchen Sie es erneut oder fragen Sie weniger auf einmal an.',
  invalid: 'Diese App hat etwas angefragt, das sie nicht kann. Bitte im Chat darum, es zu beheben.',
  unavailable: 'Dies konnte gerade nicht geladen werden. Versuchen Sie es erneut.',
};
const el: Copy = {
  connection:
    'Η συνδεδεμένη πηγή δεν είναι διαθέσιμη αυτή τη στιγμή. Ο κάτοχός της μπορεί να τη συνδέσει ξανά.',
  connectionChanged:
    'Η συνδεδεμένη πηγή άλλαξε. Ανοίξτε ξανά την εφαρμογή για να τη χρησιμοποιήσετε.',
  access: 'Δεν έχετε πλέον πρόσβαση σε αυτά τα δεδομένα.',
  appChanged: 'Η εφαρμογή άλλαξε. Ανοίξτε την ξανά για να δείτε την πιο πρόσφατη έκδοση.',
  busy: 'Πάρα πολλά αιτήματα αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο.',
  tooLarge: 'Το αίτημα είναι πολύ μεγάλο. Ζητήστε λιγότερα κάθε φορά.',
  slow: 'Η φόρτωση άργησε πολύ. Δοκιμάστε ξανά ή ζητήστε λιγότερα κάθε φορά.',
  invalid: 'Η εφαρμογή ζήτησε κάτι που δεν μπορεί να κάνει. Ζητήστε στη συνομιλία να διορθωθεί.',
  unavailable: 'Δεν ήταν δυνατή η φόρτωση αυτή τη στιγμή. Δοκιμάστε ξανά.',
};
const es: Copy = {
  connection:
    'La fuente conectada no está disponible ahora. Su propietario puede volver a conectarla.',
  connectionChanged: 'La fuente conectada ha cambiado. Vuelve a abrir la app para usarla.',
  access: 'Ya no tienes acceso a estos datos.',
  appChanged: 'Esta app ha cambiado. Vuelve a abrirla para ver la versión más reciente.',
  busy: 'Hay demasiadas solicitudes ahora. Vuelve a intentarlo en un momento.',
  tooLarge: 'La solicitud es demasiado grande. Pide menos cada vez.',
  slow: 'La carga tardó demasiado. Vuelve a intentarlo o pide menos cada vez.',
  invalid: 'Esta app pidió algo que no puede hacer. Pide en el chat que se corrija.',
  unavailable: 'No se pudo cargar ahora. Vuelve a intentarlo.',
};
const et: Copy = {
  connection: 'Ühendatud allikas pole praegu saadaval. Selle omanik saab selle uuesti ühendada.',
  connectionChanged: 'Ühendatud allikas on muutunud. Selle kasutamiseks ava rakendus uuesti.',
  access: 'Sul pole enam juurdepääsu nendele andmetele.',
  appChanged: 'Rakendus on muutunud. Uusima versiooni nägemiseks ava see uuesti.',
  busy: 'Praegu on liiga palju päringuid. Proovi hetke pärast uuesti.',
  tooLarge: 'Päring on liiga suur. Küsi korraga vähem.',
  slow: 'Laadimine võttis liiga kaua aega. Proovi uuesti või küsi korraga vähem.',
  invalid: 'Rakendus küsis midagi, mida see teha ei saa. Palu vestluses see parandada.',
  unavailable: 'Seda ei õnnestunud praegu laadida. Proovi uuesti.',
};
const fi: Copy = {
  connection:
    'Yhdistetty lähde ei ole juuri nyt käytettävissä. Sen omistaja voi yhdistää sen uudelleen.',
  connectionChanged: 'Yhdistetty lähde on muuttunut. Avaa sovellus uudelleen käyttääksesi sitä.',
  access: 'Sinulla ei ole enää pääsyä näihin tietoihin.',
  appChanged: 'Sovellus on muuttunut. Avaa se uudelleen nähdäksesi uusimman version.',
  busy: 'Pyyntöjä on juuri nyt liikaa. Yritä hetken päästä uudelleen.',
  tooLarge: 'Pyyntö on liian suuri. Pyydä kerralla vähemmän.',
  slow: 'Lataus kesti liian kauan. Yritä uudelleen tai pyydä kerralla vähemmän.',
  invalid: 'Sovellus pyysi jotain, mitä se ei voi tehdä. Pyydä chatissa korjaamaan se.',
  unavailable: 'Tätä ei voitu ladata juuri nyt. Yritä uudelleen.',
};
const fr: Copy = {
  connection:
    'La source connectée n’est pas disponible pour le moment. Son propriétaire peut la reconnecter.',
  connectionChanged: 'La source connectée a changé. Rouvrez l’app pour l’utiliser.',
  access: 'Vous n’avez plus accès à ces données.',
  appChanged: 'Cette app a changé. Rouvrez-la pour voir la dernière version.',
  busy: 'Trop de demandes pour le moment. Réessayez dans un instant.',
  tooLarge: 'Cette demande est trop volumineuse. Demandez-en moins à la fois.',
  slow: 'Le chargement a pris trop de temps. Réessayez ou demandez-en moins à la fois.',
  invalid:
    'Cette app a demandé quelque chose qu’elle ne peut pas faire. Demandez une correction dans le chat.',
  unavailable: 'Impossible de charger ceci pour le moment. Réessayez.',
};
const ga: Copy = {
  connection:
    'Níl an fhoinse cheangailte ar fáil faoi láthair. Is féidir lena húinéir í a athcheangal.',
  connectionChanged: 'Tá an fhoinse cheangailte athraithe. Oscail an aip arís chun í a úsáid.',
  access: 'Níl rochtain agat ar na sonraí seo a thuilleadh.',
  appChanged: 'Tá an aip seo athraithe. Oscail arís í chun an leagan is déanaí a fheiceáil.',
  busy: 'An iomarca iarratas faoi láthair. Bain triail eile as i gceann tamaillín.',
  tooLarge: 'Tá an t-iarratas sin rómhór. Iarr níos lú ag an am céanna.',
  slow: 'Thóg sé ró-fhada é seo a lódáil. Bain triail eile as, nó iarr níos lú ag an am céanna.',
  invalid: 'D’iarr an aip seo rud nach féidir léi a dhéanamh. Iarr sa chomhrá é a chur ina cheart.',
  unavailable: 'Níorbh fhéidir é seo a lódáil anois. Bain triail eile as.',
};
const hr: Copy = {
  connection: 'Povezani izvor trenutačno nije dostupan. Njegov ga vlasnik može ponovno povezati.',
  connectionChanged:
    'Povezani izvor se promijenio. Ponovno otvorite aplikaciju da biste ga koristili.',
  access: 'Više nemate pristup ovim podacima.',
  appChanged: 'Aplikacija se promijenila. Ponovno je otvorite da biste vidjeli najnoviju verziju.',
  busy: 'Trenutačno ima previše zahtjeva. Pokušajte ponovno za trenutak.',
  tooLarge: 'Zahtjev je prevelik. Zatražite manje odjednom.',
  slow: 'Učitavanje je trajalo predugo. Pokušajte ponovno ili zatražite manje odjednom.',
  invalid: 'Aplikacija je zatražila nešto što ne može učiniti. Zatražite ispravak u chatu.',
  unavailable: 'Ovo se trenutačno nije moglo učitati. Pokušajte ponovno.',
};
const hu: Copy = {
  connection: 'A csatlakoztatott forrás most nem érhető el. A tulajdonosa újra csatlakoztathatja.',
  connectionChanged:
    'A csatlakoztatott forrás megváltozott. Nyisd meg újra az alkalmazást a használatához.',
  access: 'Már nincs hozzáférésed ezekhez az adatokhoz.',
  appChanged: 'Az alkalmazás megváltozott. Nyisd meg újra a legújabb verzió megtekintéséhez.',
  busy: 'Most túl sok a kérés. Próbáld újra egy pillanat múlva.',
  tooLarge: 'A kérés túl nagy. Kérj egyszerre kevesebbet.',
  slow: 'A betöltés túl sokáig tartott. Próbáld újra, vagy kérj egyszerre kevesebbet.',
  invalid: 'Az alkalmazás olyat kért, amit nem tud megtenni. Kérd a javítását a csevegésben.',
  unavailable: 'Ezt most nem sikerült betölteni. Próbáld újra.',
};
const it: Copy = {
  connection:
    'La fonte collegata non è disponibile al momento. Il suo proprietario può ricollegarla.',
  connectionChanged: 'La fonte collegata è cambiata. Riapri l’App per usarla.',
  access: 'Non hai più accesso a questi dati.',
  appChanged: 'Questa App è cambiata. Riaprila per vedere la versione più recente.',
  busy: 'Troppe richieste al momento. Riprova tra poco.',
  tooLarge: 'La richiesta è troppo grande. Chiedi meno dati alla volta.',
  slow: 'Il caricamento ha richiesto troppo tempo. Riprova o chiedi meno dati alla volta.',
  invalid: 'Questa App ha chiesto qualcosa che non può fare. Chiedi in chat di correggerla.',
  unavailable: 'Impossibile caricarlo in questo momento. Riprova.',
};
const lt: Copy = {
  connection: 'Prijungtas šaltinis šiuo metu nepasiekiamas. Jo savininkas gali jį vėl prijungti.',
  connectionChanged:
    'Prijungtas šaltinis pasikeitė. Norėdami jį naudoti, iš naujo atidarykite programėlę.',
  access: 'Nebeturite prieigos prie šių duomenų.',
  appChanged: 'Programėlė pasikeitė. Atidarykite ją iš naujo, kad pamatytumėte naujausią versiją.',
  busy: 'Šiuo metu per daug užklausų. Bandykite dar kartą po akimirkos.',
  tooLarge: 'Užklausa per didelė. Prašykite mažiau vienu metu.',
  slow: 'Įkėlimas užtruko per ilgai. Bandykite dar kartą arba prašykite mažiau vienu metu.',
  invalid: 'Programėlė paprašė to, ko negali atlikti. Paprašykite pokalbyje tai pataisyti.',
  unavailable: 'Šiuo metu nepavyko įkelti. Bandykite dar kartą.',
};
const lv: Copy = {
  connection: 'Pievienotais avots pašlaik nav pieejams. Tā īpašnieks to var pievienot atkārtoti.',
  connectionChanged:
    'Pievienotais avots ir mainījies. Atveriet lietotni vēlreiz, lai to izmantotu.',
  access: 'Jums vairs nav piekļuves šiem datiem.',
  appChanged: 'Lietotne ir mainījusies. Atveriet to vēlreiz, lai redzētu jaunāko versiju.',
  busy: 'Pašlaik ir pārāk daudz pieprasījumu. Mēģiniet vēlreiz pēc brīža.',
  tooLarge: 'Pieprasījums ir pārāk liels. Pieprasiet mazāk vienlaikus.',
  slow: 'Ielāde aizņēma pārāk ilgu laiku. Mēģiniet vēlreiz vai pieprasiet mazāk vienlaikus.',
  invalid: 'Lietotne pieprasīja kaut ko, ko tā nevar izdarīt. Lūdziet to izlabot tērzēšanā.',
  unavailable: 'Pašlaik to neizdevās ielādēt. Mēģiniet vēlreiz.',
};
const mt: Copy = {
  connection: 'Is-sors konness mhux disponibbli bħalissa. Is-sid tiegħu jista’ jerġa’ jgħaqqdu.',
  connectionChanged: 'Is-sors konness inbidel. Erġa’ iftaħ l-app biex tużah.',
  access: 'M’għadx għandek aċċess għal din id-data.',
  appChanged: 'Din l-app inbidlet. Erġa’ iftaħha biex tara l-aħħar verżjoni.',
  busy: 'Wisq talbiet bħalissa. Erġa’ pprova fi ftit.',
  tooLarge: 'Din it-talba kbira wisq. Itlob inqas kull darba.',
  slow: 'Dan dam wisq biex jillowdja. Erġa’ pprova, jew itlob inqas kull darba.',
  invalid: 'Din l-app talbet xi ħaġa li ma tistax tagħmel. Itlob fiċ-chat biex tissewwa.',
  unavailable: 'Ma setax jillowdja bħalissa. Erġa’ pprova.',
};
const nl: Copy = {
  connection: 'De gekoppelde bron is nu niet beschikbaar. De eigenaar kan hem opnieuw koppelen.',
  connectionChanged: 'De gekoppelde bron is gewijzigd. Open de app opnieuw om hem te gebruiken.',
  access: 'Je hebt geen toegang meer tot deze gegevens.',
  appChanged: 'Deze app is gewijzigd. Open hem opnieuw om de nieuwste versie te zien.',
  busy: 'Er zijn nu te veel verzoeken. Probeer het zo opnieuw.',
  tooLarge: 'Dit verzoek is te groot. Vraag minder tegelijk op.',
  slow: 'Het laden duurde te lang. Probeer het opnieuw of vraag minder tegelijk op.',
  invalid: 'Deze app vroeg iets wat hij niet kan. Vraag in de chat om het te herstellen.',
  unavailable: 'Dit kon nu niet worden geladen. Probeer het opnieuw.',
};
const pl: Copy = {
  connection: 'Połączone źródło jest teraz niedostępne. Jego właściciel może je ponownie połączyć.',
  connectionChanged:
    'Połączone źródło się zmieniło. Otwórz aplikację ponownie, aby z niego korzystać.',
  access: 'Nie masz już dostępu do tych danych.',
  appChanged: 'Ta aplikacja się zmieniła. Otwórz ją ponownie, aby zobaczyć najnowszą wersję.',
  busy: 'Zbyt wiele żądań w tej chwili. Spróbuj ponownie za chwilę.',
  tooLarge: 'To żądanie jest za duże. Proś o mniej naraz.',
  slow: 'Wczytywanie trwało zbyt długo. Spróbuj ponownie lub proś o mniej naraz.',
  invalid: 'Ta aplikacja poprosiła o coś, czego nie może zrobić. Poproś na czacie o poprawkę.',
  unavailable: 'Nie udało się teraz tego wczytać. Spróbuj ponownie.',
};
const pt: Copy = {
  connection: 'A fonte ligada não está disponível agora. O proprietário pode voltar a ligá-la.',
  connectionChanged: 'A fonte ligada mudou. Volta a abrir a app para a usares.',
  access: 'Já não tens acesso a estes dados.',
  appChanged: 'Esta app mudou. Volta a abri-la para veres a versão mais recente.',
  busy: 'Demasiados pedidos neste momento. Tenta novamente daqui a pouco.',
  tooLarge: 'Este pedido é demasiado grande. Pede menos de cada vez.',
  slow: 'O carregamento demorou demasiado. Tenta novamente ou pede menos de cada vez.',
  invalid: 'Esta app pediu algo que não consegue fazer. Pede no chat para corrigir.',
  unavailable: 'Não foi possível carregar isto agora. Tenta novamente.',
};
const ro: Copy = {
  connection: 'Sursa conectată nu este disponibilă acum. Proprietarul ei o poate reconecta.',
  connectionChanged: 'Sursa conectată s-a schimbat. Redeschide aplicația ca să o folosești.',
  access: 'Nu mai ai acces la aceste date.',
  appChanged: 'Această aplicație s-a schimbat. Redeschide-o ca să vezi cea mai recentă versiune.',
  busy: 'Prea multe solicitări acum. Încearcă din nou peste câteva momente.',
  tooLarge: 'Solicitarea este prea mare. Cere mai puțin odată.',
  slow: 'Încărcarea a durat prea mult. Încearcă din nou sau cere mai puțin odată.',
  invalid: 'Această aplicație a cerut ceva ce nu poate face. Cere în chat să fie corectată.',
  unavailable: 'Nu s-a putut încărca acum. Încearcă din nou.',
};
const sk: Copy = {
  connection: 'Prepojený zdroj teraz nie je dostupný. Jeho vlastník ho môže znova pripojiť.',
  connectionChanged: 'Prepojený zdroj sa zmenil. Aplikáciu znova otvorte, aby ste ho mohli použiť.',
  access: 'K týmto údajom už nemáte prístup.',
  appChanged: 'Aplikácia sa zmenila. Znova ju otvorte a uvidíte najnovšiu verziu.',
  busy: 'Práve je príliš veľa požiadaviek. Skúste to o chvíľu znova.',
  tooLarge: 'Požiadavka je príliš veľká. Žiadajte menej naraz.',
  slow: 'Načítanie trvalo príliš dlho. Skúste to znova alebo žiadajte menej naraz.',
  invalid: 'Aplikácia žiadala niečo, čo nemôže urobiť. Požiadajte v chate o opravu.',
  unavailable: 'Teraz sa to nepodarilo načítať. Skúste to znova.',
};
const sl: Copy = {
  connection: 'Povezani vir trenutno ni na voljo. Njegov lastnik ga lahko znova poveže.',
  connectionChanged: 'Povezani vir se je spremenil. Za uporabo znova odprite aplikacijo.',
  access: 'Do teh podatkov nimate več dostopa.',
  appChanged: 'Aplikacija se je spremenila. Znova jo odprite, da vidite najnovejšo različico.',
  busy: 'Trenutno je preveč zahtev. Poskusite znova čez trenutek.',
  tooLarge: 'Zahteva je prevelika. Zahtevajte manj naenkrat.',
  slow: 'Nalaganje je trajalo predolgo. Poskusite znova ali zahtevajte manj naenkrat.',
  invalid: 'Aplikacija je zahtevala nekaj, česar ne more narediti. V klepetu prosite za popravek.',
  unavailable: 'Tega trenutno ni bilo mogoče naložiti. Poskusite znova.',
};
const sv: Copy = {
  connection: 'Den anslutna källan är inte tillgänglig just nu. Dess ägare kan ansluta den igen.',
  connectionChanged: 'Den anslutna källan har ändrats. Öppna appen igen för att använda den.',
  access: 'Du har inte längre åtkomst till de här uppgifterna.',
  appChanged: 'Appen har ändrats. Öppna den igen för att se den senaste versionen.',
  busy: 'För många förfrågningar just nu. Försök igen om en stund.',
  tooLarge: 'Förfrågan är för stor. Be om mindre åt gången.',
  slow: 'Det tog för lång tid att läsa in. Försök igen, eller be om mindre åt gången.',
  invalid: 'Appen bad om något den inte kan göra. Be om en rättning i chatten.',
  unavailable: 'Det gick inte att läsa in just nu. Försök igen.',
};

export const appRuntimeErrorsCopy: NamespaceCatalog<AppRuntimeErrorKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default appRuntimeErrorsCopy;
